import { useState, useEffect, useRef, useCallback } from 'react';
import { postTyping, getTypingStatus } from '../api/messages';

const POLL_INTERVAL_MS  = 2_000;  // check who's typing every 2 s
const SIGNAL_COOLDOWN_MS = 3_000; // send our own signal at most once per 3 s

/**
 * Manages the real-time typing indicator for a single thread.
 *
 * Returns:
 * - `typingUsers`     – usernames of the other participants currently typing
 * - `sendTypingSignal` – call this whenever the current user presses a key
 */
export const useTypingStatus = (threadId: string | null) => {
  const [typingUsers, setTypingUsers] = useState<string[]>([]);

  // Ref-based gate: avoid spamming the typing endpoint
  const canSendRef = useRef(true);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Poll the typing status for the active thread
  useEffect(() => {
    if (!threadId) {
      setTypingUsers([]);
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const users = await getTypingStatus(threadId);
        if (!cancelled) setTypingUsers(users);
      } catch {
        // silent – typing indicator is non-critical
      }
    };

    poll(); // immediate first tick
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [threadId]);

  // Cleanup cooldown timer on unmount
  useEffect(() => () => {
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
  }, []);

  /**
   * Call this on every keypress inside the message input.
   * The function internally throttles network calls to once per SIGNAL_COOLDOWN_MS.
   */
  const sendTypingSignal = useCallback(() => {
    if (!threadId || !canSendRef.current) return;

    canSendRef.current = false;
    postTyping(threadId).catch(() => {}); // fire-and-forget

    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    cooldownTimer.current = setTimeout(() => {
      canSendRef.current = true;
    }, SIGNAL_COOLDOWN_MS);
  }, [threadId]);

  return { typingUsers, sendTypingSignal };
};
